import type { Grade, Mode, ScoreBreakdown } from "./types";
import { utcDateISO } from "./rng";

export interface LockerHost {
  locker?: LockerState;
  classCode: string;
  guestStars: Record<string, number[]>;
}

export type ScoutId = "pip" | "cap" | "nib" | "ember" | "coil" | "hollow" | "tide" | "ridge" | "brass" | "quill";
export type LanternId = "tin" | "amber" | "copper" | "night" | "eclipse";
export type PackId = "canvas" | "maproll" | "survey" | "waxed";
export type StampId = "paw" | "boot" | "cleat" | "gear" | "quilltick";

export type UnlockRule =
  | { kind: "free" }
  | { kind: "marks"; n: number }
  | { kind: "marksOr"; n: number; deed: string }
  | { kind: "deed"; id: string };

export interface ScoutDef {
  id: ScoutId;
  name: string;
  blurb: string;
  src: string;
  kind: "animal" | "kid" | "clockwork";
  unlock: UnlockRule;
  ruleText: string;
}

export interface KitDef {
  id: string;
  name: string;
  slot: "lantern" | "pack" | "stamp";
  unlock: UnlockRule;
  ruleText: string;
  glow?: string;
}

export const SCOUTS: ScoutDef[] = [
  { id: "pip", name: "Pip", blurb: "Orange tabby explorer. Map tube, lantern ring.", src: "assets/scouts/pip.png", kind: "animal", unlock: { kind: "free" }, ruleText: "Starter" },
  { id: "cap", name: "Cap", blurb: "Trail runner. Worn cap, field jacket.", src: "assets/scouts/cap.png", kind: "kid", unlock: { kind: "free" }, ruleText: "Starter" },
  { id: "nib", name: "Nib", blurb: "Raven cartographer. Map in the beak.", src: "assets/scouts/nib.png", kind: "animal", unlock: { kind: "free" }, ruleText: "Starter" },
  { id: "ember", name: "Ember", blurb: "Red fox with a brass compass.", src: "assets/scouts/ember.png", kind: "animal", unlock: { kind: "marksOr", n: 12, deed: "stars6" }, ruleText: "12 Marks or 6 campaign stars" },
  { id: "coil", name: "Coil", blurb: "Banded desert gecko. Chalk on the toes.", src: "assets/scouts/coil.png", kind: "animal", unlock: { kind: "deed", id: "levels3" }, ruleText: "Finish 3 campaign levels" },
  { id: "hollow", name: "Hollow", blurb: "Owl surveyor. Tiny transit scope.", src: "assets/scouts/hollow.png", kind: "animal", unlock: { kind: "deed", id: "dailies3" }, ruleText: "Complete 3 Daily Trails" },
  { id: "tide", name: "Tide", blurb: "River otter with a tide slate.", src: "assets/scouts/tide.png", kind: "animal", unlock: { kind: "marks", n: 30 }, ruleText: "30 Marks" },
  { id: "ridge", name: "Ridge", blurb: "Lynx in a climbing harness.", src: "assets/scouts/ridge.png", kind: "animal", unlock: { kind: "deed", id: "two3starG3" }, ruleText: "3-star any two levels in grade 3+" },
  { id: "brass", name: "Brass", blurb: "Pocket lantern-automaton. Warm lens.", src: "assets/scouts/brass.png", kind: "clockwork", unlock: { kind: "marksOr", n: 50, deed: "perfectDaily" }, ruleText: "50 Marks or a perfect Daily Trail" },
  { id: "quill", name: "Quill", blurb: "Porcupine field journalist.", src: "assets/scouts/quill.png", kind: "animal", unlock: { kind: "deed", id: "stars20" }, ruleText: "Earn 20 stars across any grades" },
];

export const LANTERNS: KitDef[] = [
  { id: "tin", name: "Tin", slot: "lantern", unlock: { kind: "free" }, ruleText: "Default", glow: "rgba(255,244,220,0.45)" },
  { id: "amber", name: "Amber", slot: "lantern", unlock: { kind: "marks", n: 15 }, ruleText: "15 Marks", glow: "rgba(232,150,40,0.5)" },
  { id: "copper", name: "Copper", slot: "lantern", unlock: { kind: "marks", n: 25 }, ruleText: "25 Marks", glow: "rgba(196,92,42,0.5)" },
  { id: "night", name: "Night-glass", slot: "lantern", unlock: { kind: "deed", id: "trails5" }, ruleText: "Complete 5 trails", glow: "rgba(140,180,220,0.45)" },
  { id: "eclipse", name: "Eclipse", slot: "lantern", unlock: { kind: "marks", n: 40 }, ruleText: "40 Marks", glow: "rgba(60,140,80,0.28)" },
];

export const PACKS: KitDef[] = [
  { id: "canvas", name: "Canvas", slot: "pack", unlock: { kind: "free" }, ruleText: "Default" },
  { id: "maproll", name: "Map-roll harness", slot: "pack", unlock: { kind: "marks", n: 20 }, ruleText: "20 Marks" },
  { id: "survey", name: "Survey satchel", slot: "pack", unlock: { kind: "deed", id: "level5" }, ruleText: "Finish campaign level 5 in any grade" },
  { id: "waxed", name: "Waxed weather pack", slot: "pack", unlock: { kind: "marks", n: 35 }, ruleText: "35 Marks" },
];

export const STAMPS: KitDef[] = [
  { id: "paw", name: "Paw", slot: "stamp", unlock: { kind: "free" }, ruleText: "Default" },
  { id: "boot", name: "Boot", slot: "stamp", unlock: { kind: "marks", n: 15 }, ruleText: "15 Marks" },
  { id: "cleat", name: "Cleat", slot: "stamp", unlock: { kind: "marks", n: 20 }, ruleText: "20 Marks" },
  { id: "gear", name: "Gear-print", slot: "stamp", unlock: { kind: "deed", id: "hasBrass" }, ruleText: "Unlock Brass, then free" },
  { id: "quilltick", name: "Quill-tick", slot: "stamp", unlock: { kind: "deed", id: "hasQuill" }, ruleText: "Unlock Quill, then free" },
];

export const FREE_SCOUTS: ScoutId[] = ["pip", "cap", "nib"];

export function emptyLocker(): LockerState {
  return {
    equipped_scout: "pip",
    equipped_lantern: "tin",
    equipped_pack: "canvas",
    equipped_stamp: "paw",
    marks: 0,
    unlocked: [...FREE_SCOUTS],
    trailsFinished: 0,
    campaignClears: {},
    dailyDates: [],
    perfectDaily: false,
    firstThreeStar: [],
    firstPickDone: false,
    classroomLock: null,
    guestEarned: false,
    marksDailyClaimed: {},
  };
}

export interface LockerState {
  equipped_scout: ScoutId;
  equipped_lantern: LanternId;
  equipped_pack: PackId;
  equipped_stamp: StampId;
  marks: number;
  unlocked: string[];
  trailsFinished: number;
  campaignClears: Record<string, boolean>;
  dailyDates: string[];
  perfectDaily: boolean;
  firstThreeStar: string[];
  firstPickDone: boolean;
  classroomLock: boolean | null;
  guestEarned: boolean;
  marksDailyClaimed: Record<string, boolean>;
}

function starsOf(s: LockerHost, grade: number): number[] {
  return s.guestStars[String(grade)] ? s.guestStars[String(grade)].slice() : Array(10).fill(0);
}

export function classroomLocked(s: LockerHost): boolean {
  const L = s.locker || emptyLocker();
  if (L.classroomLock === true) return true;
  if (L.classroomLock === false) return false;
  return !!(s.classCode && s.classCode.length === 6);
}

export function activeScout(s: LockerHost): ScoutId {
  if (classroomLocked(s)) return "pip";
  const id = (s.locker || emptyLocker()).equipped_scout;
  return isUnlocked(s, id) ? id : "pip";
}

export function isUnlocked(s: LockerHost, id: string): boolean {
  const L = s.locker || emptyLocker();
  if (FREE_SCOUTS.includes(id as ScoutId)) return true;
  return L.unlocked.includes(id);
}

export function totalStars(s: LockerHost): number {
  let n = 0;
  for (let g = 0; g <= 8; g++) {
    for (const st of starsOf(s, g)) n += st || 0;
  }
  return n;
}

export function campaignLevelsFinished(s: LockerHost): number {
  const L = s.locker || emptyLocker();
  return Object.keys(L.campaignClears).length;
}

export function deedMet(s: LockerHost, id: string): boolean {
  const L = s.locker || emptyLocker();
  switch (id) {
    case "stars6":
      return totalStars(s) >= 6;
    case "stars20":
      return totalStars(s) >= 20;
    case "levels3":
      return campaignLevelsFinished(s) >= 3;
    case "dailies3":
      return L.dailyDates.length >= 3;
    case "two3starG3":
      return L.firstThreeStar.filter((k) => Number(k.split("-")[0]) >= 3).length >= 2;
    case "perfectDaily":
      return L.perfectDaily;
    case "trails5":
      return L.trailsFinished >= 5;
    case "level5":
      return Object.keys(L.campaignClears).some((k) => k.endsWith("-5"));
    case "hasBrass":
      return isUnlocked(s, "brass");
    case "hasQuill":
      return isUnlocked(s, "quill");
    default:
      return false;
  }
}

export function canUnlock(s: LockerHost, rule: UnlockRule): boolean {
  if (rule.kind === "free") return true;
  const L = s.locker || emptyLocker();
  if (rule.kind === "marks") return L.marks >= rule.n;
  if (rule.kind === "marksOr") return L.marks >= rule.n || deedMet(s, rule.deed);
  return deedMet(s, rule.id);
}

export function grantDeedUnlocks(s: LockerHost): string[] {
  const L = ensureLocker(s);
  const gained: string[] = [];
  const check = (id: string, rule: UnlockRule, name: string) => {
    if (L.unlocked.includes(id)) return;
    if (rule.kind === "free") return;
    if (rule.kind === "marks") return;
    const ok =
      rule.kind === "deed"
        ? deedMet(s, rule.id)
        : rule.kind === "marksOr"
          ? deedMet(s, rule.deed)
          : false;
    if (ok) {
      L.unlocked.push(id);
      gained.push(name);
    }
  };
  for (const sc of SCOUTS) check(sc.id, sc.unlock, sc.name);
  for (const k of [...LANTERNS, ...PACKS, ...STAMPS]) check(k.id, k.unlock, k.name);
  return gained;
}

export function buyWithMarks(s: LockerHost, id: string, price: number): { ok: boolean; error?: string } {
  const L = ensureLocker(s);
  if (L.unlocked.includes(id)) return { ok: true };
  if (L.marks < price) return { ok: false, error: "Not enough Marks." };
  L.marks -= price;
  L.unlocked.push(id);
  L.guestEarned = true;
  grantDeedUnlocks(s);
  return { ok: true };
}

export function ensureLocker(s: LockerHost): LockerState {
  if (!s.locker) s.locker = emptyLocker();
  const L = s.locker;
  for (const id of FREE_SCOUTS) if (!L.unlocked.includes(id)) L.unlocked.push(id);
  return L;
}

export interface MarksPayout {
  total: number;
  lines: { label: string; n: number }[];
}

export function payoutMarks(
  s: LockerHost,
  spec: { mode: Mode; grade: Grade; level: number | null },
  br: ScoreBreakdown,
  prevStars: number
): MarksPayout {
  const lines: { label: string; n: number }[] = [];
  if (!br.finished) return { total: 0, lines };
  lines.push({ label: "Trail finished", n: 10 });
  const acc = br.pathLength ? br.firstTry / br.pathLength : 0;
  if (acc >= 0.9) lines.push({ label: "First-try 90%+", n: 5 });
  if (br.misses === 0) lines.push({ label: "Perfect trail", n: 10 });
  if (spec.mode === "campaign" && spec.level && br.stars === 3 && prevStars === 0) {
    lines.push({ label: "First 3-star", n: 15 });
  }
  const L = ensureLocker(s);
  if (spec.mode === "daily") {
    const key = utcDateISO() + "|" + spec.grade;
    if (!L.marksDailyClaimed[key]) {
      lines.push({ label: "Daily Trail", n: 20 });
      L.marksDailyClaimed[key] = true;
    }
  }
  const total = lines.reduce((a, b) => a + b.n, 0);
  L.marks += total;
  L.trailsFinished += 1;
  L.guestEarned = true;
  if (spec.mode === "campaign" && spec.level) {
    L.campaignClears[spec.grade + "-" + spec.level] = true;
    if (br.stars === 3) {
      const k = spec.grade + "-" + spec.level;
      if (!L.firstThreeStar.includes(k)) L.firstThreeStar.push(k);
    }
  }
  if (spec.mode === "daily") {
    const d = utcDateISO();
    if (!L.dailyDates.includes(d)) L.dailyDates.push(d);
    if (br.misses === 0) L.perfectDaily = true;
  }
  return { total, lines };
}

export function lanternGlow(id: string): string {
  return LANTERNS.find((l) => l.id === id)?.glow || LANTERNS[0].glow!;
}

export function scoutById(id: string): ScoutDef {
  return SCOUTS.find((s) => s.id === id) || SCOUTS[0];
}
